import React from 'react';
import PropTypes from 'prop-types';
import ButtonCollapse from '../ButtonCollapse';
import DollarsAndCents from '../DollarsAndCents';
import CurrencyHelper from '../../Helpers/CurrencyHelper';
import Dates from '../Dates';
import ViewDateList from './ViewDateList';
import {
  FOOD_VENDOR,
  GOODS_VENDOR,
  PERFORMER,
  EXHIBITOR,
} from '../../Constants/constants';
import { listToStringDays } from '../../Helpers/ListToString';
import pluralize from '../../Helpers/Pluralize';
import { dateHelpers } from '@jauntin/utilities';

class View extends React.Component {
  constructor(props) {
    super(props);
    this.state = { expandDateList: false };
  }

  componentDidUpdate(prevProps) {
    const { expandDateList } = this.state;

    // Collapse dates list accordion whenever View component is hidden
    if (!prevProps.show && expandDateList)
      this.setExpandDateList(!expandDateList);
  }

  guests = (guestCount) =>
    `${guestCount.toString()}\xa0${pluralize(
      guestCount,
      'person',
      'people',
    )} will attend.`;

  duration = (eventDates) => {
    const days = eventDates.length;
    let firstDay;
    let lastDay;
    if (days) {
      firstDay = new Date(eventDates[0]);
      lastDay = new Date(eventDates[eventDates.length - 1]);
    } else {
      firstDay = new Date();
      lastDay = new Date();
    }
    if (days === 1)
      return `1 day long - ${dateHelpers.lettersAndNumbers(firstDay)}`;
    return `${days} days long - ${dateHelpers.lettersAndNumbers(
      firstDay,
    )} to ${dateHelpers.lettersAndNumbers(lastDay)}`;
  };

  setExpandDateList = (expand) => {
    this.setState({ expandDateList: expand });
  };

  providerText = (type, count, frequency, list) => {
    let provider = type;
    const plural = count > 1 ? 's' : '';

    if (type === PERFORMER) provider = `performer${plural}`;
    if (type === GOODS_VENDOR) provider = `vendor${plural} selling goods`;
    if (type === FOOD_VENDOR)
      provider = `vendor${plural} selling food or beverages`;
    if (type === EXHIBITOR) provider = `exhibitor${plural}`;

    // Check if count is 7:
    if (count === 7) return `There will be 7 or more ${provider}.`;

    // Check if 'everyday' is selected
    if (frequency === 'everyday')
      return `There will be ${count} ${provider} at the event everyday.`;

    // Show detailed provider list
    return `There will be ${count} ${provider} at the event: ${listToStringDays(
      list,
    )}.`;
  };

  render() {
    const {
      eventName,
      showListOfDates,
      eventDates,
      eventFrequency,
      daysOfWeek,
      eventTotalGuests,
      eventPerformersCount,
      eventGoods,
      eventFood,
      eventExhibition,
      show,
      price,
      performersList,
      goodsVendorsList,
      foodVendorsList,
      exhibitorsList,
      performersFrequency,
      goodsVendorsFrequency,
      foodVendorsFrequency,
      exhibitorsFrequency,
    } = this.props;

    let component = '';

    const { expandDateList } = this.state;

    if (show) {
      component = (
        <div className="form-row">
          <div className="col-auto w--70 font-weight-bold">
            <DollarsAndCents
              currency={new CurrencyHelper().setValue(price).convertFromInt()}
              cents={false}
            />
          </div>
          <div className="col">
            <div>{eventName}</div>
            {!showListOfDates ? (
              <div>{this.duration(eventDates)}</div>
            ) : (
              eventDates.length > 0 && (
                <>
                  <ul className="p-0 m-0 list-unstyled">
                    <Dates
                      dates={eventDates}
                      type={eventFrequency}
                      frequency={daysOfWeek}
                    />
                  </ul>
                  <ButtonCollapse
                    clickFunction={() => {
                      this.setExpandDateList(!expandDateList);
                    }}
                    showList={expandDateList}
                    title={expandDateList ? 'Hide all dates' : 'See all dates'}
                  />
                  {expandDateList && <ViewDateList dates={eventDates} />}
                </>
              )
            )}
            <div>{this.guests(eventTotalGuests)}</div>
            <div>
              {Number(eventPerformersCount) > 0 && (
                <div>
                  {this.providerText(
                    PERFORMER,
                    Number(eventPerformersCount),
                    performersFrequency,
                    performersList,
                  )}
                </div>
              )}
              {Number(eventGoods) > 0 && (
                <div>
                  {this.providerText(
                    GOODS_VENDOR,
                    Number(eventGoods),
                    goodsVendorsFrequency,
                    goodsVendorsList,
                  )}
                </div>
              )}
              {Number(eventFood) > 0 && (
                <div>
                  {this.providerText(
                    FOOD_VENDOR,
                    Number(eventFood),
                    foodVendorsFrequency,
                    foodVendorsList,
                  )}
                </div>
              )}
              {Number(eventExhibition) > 0 && (
                <div>
                  {this.providerText(
                    EXHIBITOR,
                    Number(eventExhibition),
                    exhibitorsFrequency,
                    exhibitorsList,
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    return component;
  }
}

View.propTypes = {
  eventName: PropTypes.string.isRequired,
  showListOfDates: PropTypes.bool.isRequired,
  eventDates: PropTypes.arrayOf(PropTypes.string).isRequired,
  eventFrequency: PropTypes.string.isRequired,
  daysOfWeek: PropTypes.arrayOf(PropTypes.number).isRequired,
  eventTotalGuests: PropTypes.string.isRequired,
  eventPerformersCount: PropTypes.string.isRequired,
  eventGoods: PropTypes.string.isRequired,
  eventFood: PropTypes.string.isRequired,
  eventExhibition: PropTypes.string.isRequired,
  show: PropTypes.bool.isRequired,
  price: PropTypes.number.isRequired,
  performersList: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      days: PropTypes.string,
    }),
  ),
  goodsVendorsList: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      days: PropTypes.string,
    }),
  ),
  foodVendorsList: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      days: PropTypes.string,
    }),
  ),
  exhibitorsList: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      days: PropTypes.string,
    }),
  ),
  performersFrequency: PropTypes.string,
  goodsVendorsFrequency: PropTypes.string,
  foodVendorsFrequency: PropTypes.string,
  exhibitorsFrequency: PropTypes.string,
};

View.defaultProps = {
  performersList: [],
  goodsVendorsList: [],
  foodVendorsList: [],
  exhibitorsList: [],
  performersFrequency: null,
  goodsVendorsFrequency: null,
  foodVendorsFrequency: null,
  exhibitorsFrequency: null,
};

export default View;
